<script setup lang="ts">
import { computed } from 'vue'
import { useBreakpoints } from '@/lib/utils'
import BaseButton from '@/components/ui/button/BaseButton.vue'

export interface IMercariCircleSliderItem {
	id: string | number
	image_url: string
	display_name: string
	url: string
}

const props = defineProps<{
	title: string
	items: IMercariCircleSliderItem[]
	viewAllUrl?: string
	viewAllText?: string
}>()

const emit = defineEmits<{
	'item-click': [item: IMercariCircleSliderItem]
	'view-all-click': []
}>()

const { isTabletPortrait, isLarge } = useBreakpoints()

const spaceBetween = computed(() => {
	switch (true) {
		case isLarge.value:
			return 40
		case isTabletPortrait.value:
			return 24
		default:
			return 16
	}
})

const handleItemClick = (item: IMercariCircleSliderItem) => {
	emit('item-click', item)
}

const handleViewAllClick = () => {
	emit('view-all-click')
}
</script>

<template>
	<section class="bs-container mt-32px lg:mt-[60px]">
		<div class="col-span-12">
			<div class="mb-16px flex items-center justify-between mdp:mb-32px">
				<h2
					:class="isTabletPortrait ? 'typography-h4-str' : 'typography-s1-str'"
				>
					{{ title }}
				</h2>

				<a v-if="viewAllUrl && viewAllText" :href="viewAllUrl">
					<BaseButton
						variant="tertiary"
						size="medium"
						class="!p-0 [&>span]:!border-b-0"
						@click="handleViewAllClick"
					>
						{{ viewAllText }}
					</BaseButton>
				</a>
			</div>

			<swiper-container
				slides-per-view="auto"
				:space-between="spaceBetween"
				class="-mx-20px mdp:-mx-32px lg:mx-0"
			>
				<swiper-slide
					v-for="item in items"
					:key="item.id"
					class="w-[96px] first:ml-20px last:mr-20px mdp:w-[164px] mdp:first:ml-32px mdp:last:mr-32px lg:first:ml-0 lg:last:mr-0"
				>
					<a
						class="flex flex-col items-center gap-8px !text-black !no-underline mdp:gap-12px"
						:href="item.url"
						target="_blank"
						@click="handleItemClick(item)"
					>
						<div
							class="relative h-[80px] w-[80px] overflow-hidden rounded-full bg-[linear-gradient(0deg,rgba(220,220,220,0.20)_0%,rgba(220,220,220,0.20)_100%)] mdp:h-[140px] mdp:w-[140px]"
						>
							<img
								:src="item.image_url"
								:alt="`${item.display_name}`"
								class="h-full w-full select-none rounded-full object-cover"
								loading="lazy"
							/>
							<div
								class="absolute inset-0 rounded-full bg-[linear-gradient(0deg,rgba(220,220,220,0.20)_0%,rgba(220,220,220,0.20)_100%)]"
							/>
						</div>

						<div
							class="w-full text-center mdp:truncate"
							:class="
								isTabletPortrait ? 'typography-b2-str' : 'typography-c1-str'
							"
						>
							{{ item.display_name }}
						</div>
					</a>
				</swiper-slide>
			</swiper-container>
		</div>
	</section>
</template>
